import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import apiClient from "../lib/api";
import { useI18n } from "../i18n";
import { Package, Truck, Check } from "lucide-react";

const OrderTracking = () => {
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (initialCode) => {
    setErr("");
    setLoading(true);
    try {
      const trackingCode = initialCode || code;
      const { data } = await apiClient.get(`/orders/track/${trackingCode}`, { params: email ? { email } : {} });
      setOrder(data);
      setParams({ code: trackingCode });
    } catch (e) {
      setErr(e?.response?.data?.detail || (lang === "nl" ? "Niet gevonden" : "Not found"));
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (params.get("code")) lookup(params.get("code")); /* eslint-disable-next-line */ }, []);

  return (
    <div className="max-w-[900px] mx-auto px-6 py-16" data-testid="track-page">
      <h1 className="font-display text-5xl tracking-tighter font-light">{t("track_title")}</h1>
      <p className="text-ink-muted mt-3 text-sm">{lang === "nl" ? "Voer je trackingcode in om de status te zien." : "Enter your tracking code to view status."}</p>
      <div className="mt-8 grid md:grid-cols-3 gap-3">
        <input data-testid="track-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("track_code")} className="border border-black/15 px-4 py-3 text-sm bg-white" />
        <input data-testid="track-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("track_email")} className="border border-black/15 px-4 py-3 text-sm bg-white" />
        <button data-testid="track-lookup" onClick={() => lookup()} disabled={loading || !code} className="btn-primary text-sm">{t("track_lookup")}</button>
      </div>
      {err && <div className="mt-4 text-sm text-terracotta" data-testid="track-error">{err}</div>}
      {order && (
        <div className="mt-12 bg-white border border-black/10 p-6" data-testid="track-result">
          <div className="flex items-end justify-between border-b border-black/10 pb-4">
            <div>
              <div className="font-label text-ink-muted">Order</div>
              <div className="font-display text-2xl">{order.order_id}</div>
            </div>
            <div>
              <div className="font-label text-ink-muted">{order.carrier}</div>
              <div className="font-display text-2xl">{order.tracking_code}</div>
            </div>
            <div className="text-right">
              <div className="font-label text-ink-muted">Status</div>
              <div className="font-display text-2xl capitalize">{order.status.replace("_", " ")}</div>
            </div>
          </div>
          <div className="mt-6 space-y-3">
            {(order.tracking_events || []).map((ev, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-8 h-8 bg-secondary-warm flex items-center justify-center shrink-0">
                  {i === 0 ? <Package className="w-4 h-4 text-terracotta" /> : <Truck className="w-4 h-4 text-terracotta" />}
                </div>
                <div>
                  <div className="text-sm font-medium">{ev.status}</div>
                  <div className="text-xs text-ink-muted">{ev.timestamp?.slice(0, 19).replace("T", " ")}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 border-t border-black/10 pt-4 text-sm">
            <div className="font-label text-ink-muted mb-2">{lang === "nl" ? "Items" : "Items"}</div>
            {order.lines.map((l, i) => (
              <div key={i} className="flex justify-between py-1.5"><span>{l.qty} × {lang === "nl" ? l.name_nl : l.name_en}</span><span>€{l.line_total.toFixed(2)}</span></div>
            ))}
            <div className="border-t border-black/10 mt-2 pt-2 flex justify-between font-display"><span>{t("total")}</span><span>€{order.grand_total.toFixed(2)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTracking;
